/**
 * DesktopSidebar
 *
 * Rendered on the left edge of the screen when windowWidth ≥ 1024px.
 * Mirrors TwoLevelTabBar's navigation structure but displayed vertically.
 *
 * Account level:   AI Assistant · Plans · Settings
 * Project level:   Back · Home · Chat · Care (Calendar/Meds/Reminders) ·
 *                  Docs (Files/Vault/Surveys/Benefits) ·
 *                  Team (Members/Contacts/Chat) · Settings
 *
 * Uses navigationRef (outside NavigationContainer) for navigation and
 * NavigationStateContext to read the current route.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useTheme } from '../hooks/useTheme';
import { getWhitelabelPalette } from '../theme/getWhitelabelPalette';
import { navigationRef } from '../navigation/navigationRef';
import { PARTNER_WORKSPACE_TABS, type PartnerWorkspaceTab } from '../navigation/partnerWorkspace';
import { getActiveRouteInfo, useNavState } from '../contexts/NavigationStateContext';
import { useRoutes } from '../navigation/useRoutes';
import OrbAvatar from './OrbAvatar';
import Icon from './Icon';
import PartnerLogo from './PartnerLogo';
import { TOUR_ANCHORS } from '../tour/anchors';
import { useTour } from '../tour/TourContext';
import type { RootState } from '../store';
import AppDownloadMenu from './AppDownloadMenu';

// ── Constants ─────────────────────────────────────────────────────────────────

// Resting width — icon rail only. Hovering anywhere on the sidebar expands it
// to SIDEBAR_WIDTH and reveals labels. Native platforms never render this
// component (DesktopLayout gates it behind DESKTOP_BREAKPOINT) so the
// hover-driven UX is web/electron-only.
const RAIL_WIDTH    = 64;
const SIDEBAR_WIDTH = 240;

type SubItem = { screen: string; label: string; icon: [string, string] };
type Category = { key: string; label: string; icon: [string, string]; items: SubItem[]; directScreen?: string };

const PARTNER_TAB_NAMES = new Set<string>(PARTNER_WORKSPACE_TABS.map((tab) => tab.name));

const PROJECT_CATEGORIES: Category[] = [
  {
    key: 'chat',
    // Branded "Ellie" rather than "Chat" so this AI-assistant entry
    // doesn't collide with the member↔member Chat surface inside the
    // Team section below. The sparkles icon already signals "AI"; the
    // label name now matches the same brand we use everywhere else
    // (e.g. "Powered by Ellie" on search responses).
    label: 'Ellie',
    icon: ['fas', 'sparkles'],
    items: [],
    directScreen: 'ProjectChat',
  },
  {
    key: 'care',
    label: 'Care',
    icon: ['fad', 'heart-pulse'],
    items: [
      { screen: 'ProjectCalendar',    label: 'Calendar',    icon: ['fad', 'calendar-days'] },
      { screen: 'ProjectMedications', label: 'Medications', icon: ['fad', 'pills'] },
      { screen: 'ProjectReminders',   label: 'Reminders',   icon: ['fad', 'bell'] },
    ],
  },
  {
    key: 'docs',
    label: 'Docs',
    icon: ['fad', 'folder-open'],
    items: [
      { screen: 'ProjectFiles',    label: 'Files',    icon: ['fad', 'file'] },
      { screen: 'ProjectVault',    label: 'Vault',    icon: ['fad', 'vault'] },
      { screen: 'ProjectSurveys',  label: 'Surveys',  icon: ['fad', 'list-check'] },
      { screen: 'ProjectBenefits', label: 'Benefits', icon: ['fad', 'shield-check'] },
    ],
  },
  {
    key: 'team',
    label: 'Team',
    icon: ['fad', 'users'],
    items: [
      { screen: 'ProjectMembers',  label: 'Members',  icon: ['fad', 'user-group'] },
      { screen: 'ProjectContacts', label: 'Contacts', icon: ['fad', 'address-book'] },
      // Chat with the people on this plan. Same surface as
      // ContactsScreen's Chat tab — we route to ProjectTeam with
      // tab='chat' so the in-screen segment is pre-selected. The
      // sibling Members/Contacts entries do the equivalent for their
      // own tabs (see goToPlansScreen's tab-param map below).
      { screen: 'ProjectTeam',     label: 'Chat',     icon: ['fad', 'comments'] },
    ],
  },
];

/** Sidebar categories the tour stops at, keyed by `Category.key`. */
const CATEGORY_ANCHORS: Record<string, string | undefined> = {
  care: TOUR_ANCHORS.navProjectCare,
  docs: TOUR_ANCHORS.navProjectDocs,
  team: TOUR_ANCHORS.navProjectTeam,
};

const SCREEN_TO_CATEGORY: Record<string, string> = {
  ProjectChat:     'chat',
  ProjectHome:     'home',
  ProjectSettings: 'settings',
};
for (const cat of PROJECT_CATEGORIES) {
  for (const item of cat.items) {
    SCREEN_TO_CATEGORY[item.screen] = cat.key;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DesktopSidebar() {
  const baseTheme  = useTheme();
  const partner    = useSelector((s: RootState) => s.partners.partner);
  const currentAccountId = useSelector(
    (s: RootState) => (s.session as any)?.account?.account_id,
  );
  // A partner row can remain cached while the user is accessing a client
  // account. Scope the palette just like the navigation mode so one partner's
  // branding cannot bleed into the client's own workspace.
  const activeAccountPartner = partner?.account_id === currentAccountId ? partner : null;
  const theme      = useMemo(
    () => getWhitelabelPalette(baseTheme, activeAccountPartner),
    [baseTheme, activeAccountPartner],
  );
  const styles     = useMemo(() => makeStyles(theme), [theme]);
  const navState   = useNavState();
  const routeInfo  = useMemo(() => getActiveRouteInfo(navState), [navState]);

  // Hover-to-expand. Pointer events fire on web/electron only; native is fine
  // ignoring them. We toggle a single `expanded` flag that drives both the
  // sidebar width and the opacity / display of every label-bearing element.
  const [expanded, setExpanded] = useState(false);
  const onPointerEnter = useCallback(() => setExpanded(true), []);
  const onPointerLeave = useCallback(() => setExpanded(false), []);
  const widthStyle = { width: expanded ? SIDEBAR_WIDTH : RAIL_WIDTH };

  // Account-level chat is gated (≥ MIN_PLANS_FOR_ACCOUNT_CHAT plans + permission)
  // and the 'AI' route only exists when that gate passes — same source of truth the
  // tab navigator uses. The identity orb below navigates to 'AI', so when the route
  // isn't registered, tapping it is a silent no-op. Hide the orb in that case, exactly
  // like the mobile bottom bar does (CustomTabBar's `showOrb = hasAITab && …`), instead
  // of showing a dead control.
  const { tabs } = useRoutes();
  const hasAccountChat = tabs.some((t) => t.name === 'AI');
  // Account Home is intentionally absent for one-plan accounts. Keep the
  // desktop rail in lockstep with the registered routes so returning to the
  // plans list does not expose a button whose navigation target does not exist.
  const hasAccountHome = tabs.some((t) => t.name === 'Home');

  const isStreaming = useSelector((s: RootState) => !!(s.accountAssistant as any)?.streamingQuery);
  const systemName: string = useSelector(
    (s: RootState) => (s.session?.settings as any)?.system_name || (s.session?.account as any)?.system_name || 'Ellie',
  );
  const navigationPersonas: string[] = useSelector(
    (s: RootState) => ((s.session as any).navigationPersonas as string[]) ?? [],
  );

  // Partner-mode signals. Detected from the active tab (which will be
  // one of PARTNER_TAB_NAMES whenever PartnerTabNavigator is mounted)
  // PLUS a partner-signal disambiguator (so a customer on the customer
  // 'Plans' tab doesn't trip the partner swap). The previous version
  // required the redux `partner` row (populated by /partners/me) — but
  // that endpoint round-trips, so on cold-load the sidebar briefly
  // rendered customer tiles until the response landed, then snapped to
  // partner tiles. ED-2336.
  //
  // session.account.partner is redux-persist-hydrated synchronously on
  // cold launch (it's a column on the accounts row), so accepting it
  // alongside the rich partner row eliminates the flash. For partner
  // MEMBERS the active account is the partner owner's account, so
  // account.partner is true for them too (which also drives ED-2335).
  const accountIsPartner = useSelector(
    (s: RootState) => !!(s.session as any)?.account?.partner,
  );
  // Partner-Access flag — set by switchToCustomerAccount when a partner
  // taps "Access" on a customer plan card (PR #733 / ED-2354). While
  // this is true the partner is impersonating the customer's view, and
  // the sidebar must render the CUSTOMER's nav, not the partner's, so
  // the experience matches what the customer themselves would see.
  //
  // Without this guard the persisted `partners.partner` row from the
  // partner's own /partners/me fetch keeps the OR below truthy even
  // though session.account.partner has already flipped to false on the
  // customer's row — the sidebar stays stuck in partner mode while the
  // user is clearly inside a customer project.
  const viaPartnerAccess = useSelector(
    (s: RootState) => !!((s as any).accounts?.current?.via_partner_access),
  );
  const {
    isLoggedIn, activeTab, activePlansScreen, projectId, project, projectName, activePlansParams,
  } = routeInfo;

  // The partner's /partners/me row is account-scoped (partner.account_id is the
  // partner's OWN account). Only treat the cached row as a partner signal when
  // it matches the active account — otherwise it stays truthy after a partner
  // switches INTO a customer account and wrongly flips the sidebar to partner
  // nav on the shared 'Plans' tab.
  const ownPartnerAccount = !!partner
    && partner.status === 'active'
    && !!partner.account_id
    && partner.account_id === currentAccountId;

  // Two-factor partner detection: the activeTab must be one of the partner-side
  // tabs AND the ACTIVE account must be the partner's own — satisfied by EITHER
  // (a) the account-scoped /partners/me row (ownPartnerAccount), or (b) the
  // synchronously-hydrated accounts.partner column (accountIsPartner, which is
  // per-account: false on a customer's row). Both are account-specific, so a
  // partner inside a customer account correctly gets the customer nav.
  //
  // viaPartnerAccess is kept as an explicit short-circuit for the Access flow,
  // but is no longer load-bearing: it lives on accounts.current and gets wiped
  // by any account refetch (the backend doesn't know the client-only flag), so
  // relying on it alone left the sidebar stuck in partner mode once it cleared
  // (clicking 'Plans' → partner nav inside the customer shell, dead nav items).
  // Key off "not on a customer tab" rather than "is on a partner tab". At the
  // root URL react-navigation renders PartnerHome via initialRouteName, but the
  // Main navigator's nested state hasn't materialized yet, so getActiveRouteInfo
  // returns activeTab=null — the old `PARTNER_TAB_NAMES.has(activeTab)` gate then
  // failed and the sidebar dropped to the 2-item customer nav until the partner
  // clicked something (which fires a real navigate that populates the state).
  // A null/unresolved activeTab on the partner's OWN account should stay partner.
  // The account-scoped signals (accountIsPartner / ownPartnerAccount are false on
  // a customer's row) still keep a partner-inside-a-customer-account on the
  // customer nav, and viaPartnerAccess short-circuits the Access flow.
  const onCustomerTab = !!activeTab && !PARTNER_TAB_NAMES.has(activeTab);
  const inPartnerContext = !viaPartnerAccess
    && (ownPartnerAccount || accountIsPartner)
    && !onCustomerTab;

  const inProjectContext = activeTab === 'Plans' && activePlansScreen != null
    && activePlansScreen !== 'ProjectsList' && activePlansScreen !== 'CreatePlan';

  // ── Navigation helpers ──────────────────────────────────────────────────────

  // Desktop-only guided tour. `available` is false when there's no provider
  // (i.e. never, in practice — DesktopLayout mounts one — but the launcher
  // stays defensive so this component keeps working if it's rendered bare).
  const tour = useTour();

  const goToTab = useCallback((tab: string) => {
    if (!navigationRef.isReady()) return;
    try {
      // Re-tapping the Plans tab from inside a nested Plans screen
      // (CreatePlan, ProjectHome, etc.) must pop back to ProjectsList.
      // navigate('Main', { screen: 'Plans' }) alone is a no-op when the
      // Plans tab is already active — the user sees the button click but
      // nothing happens (ED-2138).
      if (tab === 'Plans') {
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectsList' },
        });
        return;
      }
      // Same as Plans: tapping the Settings gear must land on the Settings
      // index, not whatever nested screen the stack last sat on. Without the
      // explicit reset, a user who opened Billing stays stuck on Billing every
      // time they tap Settings (the bare navigate is a no-op on the active
      // tab), with no way back to the Settings menu.
      if (tab === 'Settings') {
        (navigationRef as any).navigate('Main', {
          screen: 'Settings',
          params: { screen: 'SettingsHome' },
        });
        return;
      }
      (navigationRef as any).navigate('Main', { screen: tab });
    } catch {}
  }, []);

  const goToPlansScreen = useCallback((screen: string, extraParams?: Record<string, unknown>) => {
    if (!navigationRef.isReady()) return;
    // Refuse to navigate to a project-scoped screen without a real project.
    // Without this, callers (e.g. clicking a sidebar item while routeInfo is
    // briefly stale during a tab transition) can fire navigate(...) with
    // `projectId: null, project: null, projectName: ''`, which on web
    // produces URLs like `/project/undefined/calendar?project=null&projectName=`
    // and triggers a wave of failing API calls in the destination screen.
    if (!projectId) return;
    try {
      const params: Record<string, unknown> = { projectId, project, ...extraParams };
      if (screen === 'ProjectCalendar') params.projectName = projectName;
      if (screen === 'ProjectReminders') params.projectName = projectName;
      if (screen === 'ProjectMembers') params.tab = 'members';
      if (screen === 'ProjectContacts') params.tab = 'contacts';
      // ProjectTeam is the canonical Team route; from the sidebar it
      // means "open the member-chat tab" since Members/Contacts have
      // their own sidebar entries.
      if (screen === 'ProjectTeam') params.tab = 'chat';
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen, params },
      });
    } catch {}
  }, [projectId, project, projectName]);

  // ── Category ordering ───────────────────────────────────────────────────────

  const orderedCategories = useMemo(() => {
    if (!navigationPersonas.length) return PROJECT_CATEGORIES;
    const priorityMap: Record<string, number> = {};
    for (const persona of navigationPersonas) {
      if (['meds', 'reminders', 'calendar'].includes(persona))
        priorityMap['care'] = Math.min(priorityMap['care'] ?? Infinity, 0);
      else if (['docs', 'benefits'].includes(persona))
        priorityMap['docs'] = Math.min(priorityMap['docs'] ?? Infinity, 1);
      else if (['comms', 'family'].includes(persona))
        priorityMap['team'] = Math.min(priorityMap['team'] ?? Infinity, 2);
      else if (persona === 'ai')
        priorityMap['chat'] = Math.min(priorityMap['chat'] ?? Infinity, 0);
    }
    if (!Object.keys(priorityMap).length) return PROJECT_CATEGORIES;
    return [...PROJECT_CATEGORIES].sort((a, b) => {
      const pa = priorityMap[a.key] ?? 99;
      const pb = priorityMap[b.key] ?? 99;
      if (pa !== pb) return pa - pb;
      return PROJECT_CATEGORIES.findIndex((c) => c.key === a.key) - PROJECT_CATEGORIES.findIndex((c) => c.key === b.key);
    });
  }, [navigationPersonas]);

  if (!isLoggedIn) return null;

  // ── Partner-level nav ──────────────────────────────────────────────────────
  // Mobile and desktop use the same five workspace destinations. Each click
  // explicitly targets that tab's root so re-tapping a destination reliably
  // leaves any prior detail screen.
  if (inPartnerContext) {
    const roots: Record<string, string> = {
      Home: 'PartnerHome',
      Clients: 'PartnerCustomers',
      Work: 'PartnerWork',
      Inbox: 'PartnerMessages',
      More: 'PartnerMore',
    };
    const goToPartnerScreen = (item: PartnerWorkspaceTab) => {
      if (!navigationRef.isReady()) return;
      try {
        (navigationRef as any).navigate('Main', {
          screen: item.name,
          params: { screen: roots[item.name] },
        });
      } catch {}
    };
    const company = (partner as any)?.company_name || 'Partner';

    return (
      <View
        id={TOUR_ANCHORS.sidebar}
      style={[styles.sidebar, widthStyle, { backgroundColor: theme.colors.sidebarSurface, borderRightColor: theme.colors.sidebarBorder }]}
        {...({ onMouseEnter: onPointerEnter, onMouseLeave: onPointerLeave } as any)}
      >
        {Platform.OS === 'web' && <View style={styles.titleBarSpacer} />}

        {/* Use the same uploaded logo and branded accent as the mobile shell. */}
        <View style={styles.brandSection}>
          <View style={expanded ? styles.orbRow : styles.orbRowCollapsed}>
            <View style={styles.orbWrap}>
              <PartnerLogo partner={partner} size={28} radius={8} fallbackBackground={theme.colors.primary} />
            </View>
            {expanded && (
              <View style={styles.orbTextBlock}>
                <Text style={[styles.orbLabel, { color: theme.colors.text }]} numberOfLines={1}>
                  {company}
                </Text>
                <Text style={[styles.orbSublabel, { color: theme.colors.muted }]}>
                  Partner workspace
                </Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.sectionDivider} />

        <View style={styles.navSection}>
          {PARTNER_WORKSPACE_TABS.map((item) => (
            <NavItem
              key={item.name}
              id={item.name === 'Clients' ? TOUR_ANCHORS.navPlans : undefined}
              label={item.label}
              icon={item.sidebarIcon}
              active={activeTab === item.name}
              onPress={() => goToPartnerScreen(item)}
              theme={theme}
              styles={styles}
              expanded={expanded}
            />
          ))}
        </View>

        <View style={{ flex: 1 }} />
        <View style={styles.sectionDivider} />
        <View style={styles.navSection}>
          <AppDownloadMenu
            expanded={expanded}
            onRequestExpand={() => setExpanded(true)}
          />
          {tour.available && !tour.active && (
            <NavItem
              label="Take a tour"
              icon={['fad', 'compass']}
              active={false}
              onPress={tour.start}
              dot={tour.isFirstRun}
              theme={theme}
              styles={styles}
              expanded={expanded}
            />
          )}
        </View>
        <View style={styles.bottomPad} />
      </View>
    );
  }

  // ── Account-level nav ──────────────────────────────────────────────────────

  if (!inProjectContext) {
    return (
      <View
        id={TOUR_ANCHORS.sidebar}
      style={[styles.sidebar, widthStyle, { backgroundColor: theme.colors.sidebarSurface, borderRightColor: theme.colors.sidebarBorder }]}
        // RN-Web maps these to native DOM mouse events. No-ops on native, where
        // this component never renders anyway.
        {...({ onMouseEnter: onPointerEnter, onMouseLeave: onPointerLeave } as any)}
      >
        {Platform.OS === 'web' && <View style={styles.titleBarSpacer} />}

        {/* AI Assistant — top identity row. Only when account chat is reachable
            (≥ MIN_PLANS_FOR_ACCOUNT_CHAT); otherwise tapping it is a dead no-op. */}
        {hasAccountChat && (
          <>
            <View style={styles.brandSection}>
              <TouchableOpacity
                id={TOUR_ANCHORS.navEllie}
                style={[
                  expanded ? styles.orbRow : styles.orbRowCollapsed,
                  activeTab === 'AI' && styles.orbRowActive,
                ]}
                onPress={() => goToTab('AI')}
                activeOpacity={0.8}
                // Reachable by assistive tech (and e2e) in the collapsed icon-rail,
                // where the "{name} · AI Assistant" text is hidden.
                accessibilityRole="button"
                accessibilityLabel={`${systemName} AI Assistant`}
              >
                <View style={styles.orbWrap}>
                  <OrbAvatar size={28} thinking={isStreaming} />
                </View>
                {expanded && (
                  <View style={styles.orbTextBlock}>
                    <Text style={[styles.orbLabel, { color: theme.colors.text }]} numberOfLines={1}>
                      {systemName}
                    </Text>
                    <Text style={[styles.orbSublabel, { color: theme.colors.muted }]}>
                      AI Assistant
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.sectionDivider} />
          </>
        )}

        {/* Main nav */}
        <View style={styles.navSection}>
          {hasAccountHome && (
            <NavItem
              label="Home"
              icon={['fad', 'house']}
              active={activeTab === 'Home'}
              onPress={() => goToTab('Home')}
              theme={theme}
              styles={styles}
              expanded={expanded}
            />
          )}
          <NavItem
            id={TOUR_ANCHORS.navPlans}
            label="Plans"
            icon={['fad', 'rectangle-list']}
            active={activeTab === 'Plans'}
            onPress={() => goToTab('Plans')}
            theme={theme}
            styles={styles}
            expanded={expanded}
          />
        </View>

        {/* Tour launcher + Settings pinned to bottom. The tour never starts on
            its own; a dot marks it until it's been opened once. */}
        <View style={{ flex: 1 }} />
        <View style={styles.sectionDivider} />
        <View style={styles.navSection}>
          <AppDownloadMenu
            expanded={expanded}
            onRequestExpand={() => setExpanded(true)}
          />
          {tour.available && !tour.active && (
            <NavItem
              label="Take a tour"
              icon={['fad', 'compass']}
              active={false}
              onPress={tour.start}
              dot={tour.isFirstRun}
              theme={theme}
              styles={styles}
              expanded={expanded}
            />
          )}
          <NavItem
            id={TOUR_ANCHORS.navSettings}
            label="Settings"
            icon={['fad', 'gear']}
            active={activeTab === 'Settings'}
            onPress={() => goToTab('Settings')}
            theme={theme}
            styles={styles}
            expanded={expanded}
          />
        </View>
        <View style={styles.bottomPad} />
      </View>
    );
  }

  // ── Project-level nav ──────────────────────────────────────────────────────

  return (
    <View
      id={TOUR_ANCHORS.sidebar}
      style={[styles.sidebar, widthStyle, { backgroundColor: theme.colors.sidebarSurface, borderRightColor: theme.colors.sidebarBorder }]}
      {...({ onMouseEnter: onPointerEnter, onMouseLeave: onPointerLeave } as any)}
    >
      {Platform.OS === 'web' && <View style={styles.titleBarSpacer} />}

      {/* Project header — collapsed away in icon-rail mode.
          The "< All Plans" back row that used to sit above this was removed
          — the in-screen header already shows a back affordance, and having
          two stacked "go back" treatments confused testers. */}
      {!!projectName && expanded && (
        <View style={styles.projectHeader}>
          <Text style={[styles.projectName, { color: theme.colors.text }]} numberOfLines={2}>
            {projectName}
          </Text>
        </View>
      )}

      <View style={styles.sectionDivider} />

      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Home */}
        <View style={styles.navSection}>
          <NavItem
            label="Home"
            icon={['fad', 'house']}
            active={activePlansScreen === 'ProjectHome'}
            onPress={() => goToPlansScreen('ProjectHome')}
            theme={theme}
            styles={styles}
            expanded={expanded}
          />
        </View>

        {/* Categories — all always expanded */}
        {orderedCategories.map((cat) => {
          // Chat is a direct screen — show as a regular nav item
          if (cat.directScreen && !cat.items.length) {
            const isActive = activePlansScreen === cat.directScreen;
            return (
              <View key={cat.key} style={styles.navSection}>
                <NavItem
                  id={cat.key === 'chat' ? TOUR_ANCHORS.navProjectEllie : undefined}
                  label={cat.label}
                  icon={cat.icon}
                  active={isActive}
                  onPress={() => goToPlansScreen(cat.directScreen!)}
                  theme={theme}
                  styles={styles}
                  expanded={expanded}
                />
              </View>
            );
          }

          return (
            <View key={cat.key} id={CATEGORY_ANCHORS[cat.key]} style={styles.categoryGroup}>
              {expanded && (
                <SectionLabel label={cat.label} theme={theme} styles={styles} />
              )}
              {cat.items.map((item) => {
                const isFocused = (() => {
                  // Team's three sub-items (Members / Contacts / Chat) all
                  // render ContactsScreen; the active sub-item is whichever
                  // matches the in-screen tab param, regardless of which
                  // route alias (ProjectMembers / ProjectContacts /
                  // ProjectTeam) is currently mounted.
                  if (
                    item.screen === 'ProjectMembers' ||
                    item.screen === 'ProjectContacts' ||
                    item.screen === 'ProjectTeam'
                  ) {
                    const onTeam =
                      activePlansScreen === 'ProjectMembers' ||
                      activePlansScreen === 'ProjectContacts' ||
                      activePlansScreen === 'ProjectTeam';
                    if (!onTeam) return false;
                    const routeTab = (activePlansParams as any)?.tab ??
                      (activePlansScreen === 'ProjectContacts'
                        ? 'contacts'
                        : activePlansScreen === 'ProjectTeam'
                          ? 'chat'
                          : 'members');
                    if (item.screen === 'ProjectMembers')  return routeTab === 'members';
                    if (item.screen === 'ProjectContacts') return routeTab === 'contacts';
                    return routeTab === 'chat';
                  }
                  return activePlansScreen === item.screen;
                })();

                return (
                  <SubNavItem
                    key={item.screen}
                    label={item.label}
                    icon={item.icon}
                    active={isFocused}
                    onPress={() => goToPlansScreen(item.screen)}
                    theme={theme}
                    styles={styles}
                    expanded={expanded}
                  />
                );
              })}
            </View>
          );
        })}
      </ScrollView>

      {/* Tour launcher + Settings pinned to bottom */}
      <View style={styles.sectionDivider} />
      <View style={styles.navSection}>
        <AppDownloadMenu
          expanded={expanded}
          onRequestExpand={() => setExpanded(true)}
        />
        {tour.available && !tour.active && (
          <NavItem
            label="Take a tour"
            icon={['fad', 'compass']}
            active={false}
            onPress={tour.start}
            dot={tour.isFirstRun}
            theme={theme}
            styles={styles}
            expanded={expanded}
          />
        )}
        <NavItem
          id={TOUR_ANCHORS.navSettings}
          label="Settings"
          icon={['fad', 'gear']}
          active={activePlansScreen === 'ProjectSettings'}
          onPress={() => goToPlansScreen('ProjectSettings')}
          theme={theme}
          styles={styles}
          expanded={expanded}
        />
      </View>
      <View style={styles.bottomPad} />
    </View>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function NavItem({
  label, icon, active, onPress, theme, styles, expanded, id, dot,
}: {
  label: string;
  icon: [string, string];
  active: boolean;
  onPress: () => void;
  theme: ReturnType<typeof import('../hooks/useTheme').useTheme>;
  styles: ReturnType<typeof makeStyles>;
  expanded: boolean;
  /** Tour anchor id. Maps to the DOM id on web; inert on native. */
  id?: string;
  /** Small unread-style dot — used once, to point out the tour launcher. */
  dot?: boolean;
}) {
  return (
    <TouchableOpacity
      id={id}
      style={[
        expanded ? styles.navItem : styles.navItemCollapsed,
        active && styles.navItemActive,
        active && { backgroundColor: theme.colors.sidebarActive },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      // Keep the label reachable to assistive tech (and e2e) even in the
      // collapsed icon-rail mode, where the visible <Text> label is hidden.
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {/* Accent bar only in expanded mode — background tint is enough indicator
          in icon-rail mode and the bar would push the icon off-center. */}
      {expanded && (
        <View style={[styles.accentBar, active && { backgroundColor: theme.colors.primary }]} />
      )}
      <View>
        <Icon
          icon={icon as any}
          size={22}
          color={active ? theme.colors.primary : theme.colors.muted}
        />
        {dot && <View style={[styles.navDot, { backgroundColor: theme.colors.primary }]} />}
      </View>
      {expanded && (
        <Text style={[
          styles.navItemLabel,
          { color: active ? theme.colors.primary : theme.colors.text },
        ]}>
          {label}
        </Text>
      )}
    </TouchableOpacity>
  );
}

function SubNavItem({
  label, icon, active, onPress, theme, styles, expanded,
}: {
  label: string;
  icon: [string, string];
  active: boolean;
  onPress: () => void;
  theme: ReturnType<typeof import('../hooks/useTheme').useTheme>;
  styles: ReturnType<typeof makeStyles>;
  expanded: boolean;
}) {
  return (
    <TouchableOpacity
      style={[
        expanded ? styles.subNavItem : styles.subNavItemCollapsed,
        active && styles.subNavItemActive,
        active && { backgroundColor: theme.colors.sidebarActiveSub },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      // Reachable by assistive tech (and e2e) in the collapsed icon-rail mode,
      // where the visible <Text> label is hidden. Matches NavItem.
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {expanded && (
        <View style={[styles.subAccentBar, active && { backgroundColor: theme.colors.primary }]} />
      )}
      <Icon
        icon={icon as any}
        size={18}
        color={active ? theme.colors.primary : theme.colors.muted}
      />
      {expanded && (
        <Text style={[
          styles.subNavItemLabel,
          { color: active ? theme.colors.primary : theme.colors.text },
        ]}>
          {label}
        </Text>
      )}
    </TouchableOpacity>
  );
}

function SectionLabel({
  label, theme, styles,
}: {
  label: string;
  theme: ReturnType<typeof import('../hooks/useTheme').useTheme>;
  styles: ReturnType<typeof makeStyles>;
}) {
  return (
    <Text style={[styles.sectionLabel, { color: theme.colors.muted }]}>
      {label.toUpperCase()}
    </Text>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

function makeStyles(theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) {
  return StyleSheet.create({
    sidebar: {
      borderRightWidth: StyleSheet.hairlineWidth,
      flexDirection: 'column',
      overflow: 'hidden',
      ...Platform.select({
        web: {
          boxShadow: '0 10px 30px rgba(0,0,0,0.22), 1px 0 0 ' + theme.colors.sidebarBorder,
          backdropFilter: 'blur(18px) saturate(1.15)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.15)',
          // Smooth width transition on hover-to-expand. Native ignores these.
          transitionProperty: 'width',
          transitionDuration: '180ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'width',
        } as any,
        default: {},
      }),
    },

    // macOS traffic-light clearance
    titleBarSpacer: { height: 28 },

    // First-run marker on the tour launcher. Sits on the icon so it reads the
    // same in the collapsed rail as it does expanded.
    navDot: {
      position: 'absolute',
      top: -2,
      right: -3,
      width: 8,
      height: 8,
      borderRadius: 4,
    },

    // ── Brand / AI orb section ────────────────────────────────────────────────
    brandSection: {
      paddingTop: 4,
      paddingBottom: 2,
    },
    orbRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      paddingHorizontal: 16,
      paddingVertical: 11,
      marginHorizontal: 6,
      borderRadius: 12,
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    orbRowCollapsed: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 11,
      marginHorizontal: 6,
      borderRadius: 12,
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    orbRowActive: {
      backgroundColor: theme.colors.sidebarActive,
    },
    orbWrap: {
      width: 28,
      height: 28,
      borderRadius: 14,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    orbTextBlock: { flex: 1 },
    orbLabel: {
      fontSize: 13,
      fontWeight: '600',
      lineHeight: 16,
    },
    orbSublabel: {
      fontSize: 11,
      marginTop: 1,
      lineHeight: 14,
    },

    // ── Dividers ──────────────────────────────────────────────────────────────
    sectionDivider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: theme.colors.sidebarBorder,
      marginHorizontal: 14,
      marginVertical: 5,
    },

    // ── Nav sections ─────────────────────────────────────────────────────────
    navSection: {
      paddingHorizontal: 6,
      paddingVertical: 2,
    },

    scrollContent: {
      paddingBottom: 8,
    },

    // ── Project header ────────────────────────────────────────────────────────
    projectHeader: {
      paddingHorizontal: 16,
      // Padded down from the title-bar spacer / first nav row.  Was 4 when
      // the now-removed "< All Plans" back row sat above it; bumped to 14
      // so the project name doesn't crowd the window-chrome edge.
      paddingTop: 14,
      paddingBottom: 10,
    },
    projectName: {
      fontSize: 15,
      fontWeight: '700',
      letterSpacing: -0.2,
      lineHeight: 20,
    },

    // ── Category group ────────────────────────────────────────────────────────
    categoryGroup: {
      paddingHorizontal: 6,
      paddingTop: 12,
      paddingBottom: 2,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '600',
      letterSpacing: 0.5,
      paddingHorizontal: 12,
      paddingBottom: 6,
    },

    // ── Nav item (top-level) ──────────────────────────────────────────────────
    navItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      paddingVertical: 10,
      paddingRight: 12,
      borderRadius: 11,
      marginBottom: 2,
      overflow: 'hidden',
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    // Collapsed (icon-rail) variant: centered icon, no accent bar / label.
    navItemCollapsed: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 10,
      borderRadius: 11,
      marginBottom: 2,
      overflow: 'hidden',
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    navItemActive: {},
    // Bar height tracks the icon size (22) so the active indicator visually
    // brackets the icon rather than floating below its centerline.
    accentBar: {
      width: 3,
      height: 22,
      borderRadius: 2,
      backgroundColor: 'transparent',
      marginLeft: 2,
    },
    navItemLabel: {
      fontSize: 14,
      fontWeight: '500',
      flex: 1,
    },

    // ── Sub nav item ──────────────────────────────────────────────────────────
    subNavItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 8,
      paddingRight: 12,
      borderRadius: 10,
      marginBottom: 2,
      overflow: 'hidden',
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    subNavItemCollapsed: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 8,
      borderRadius: 10,
      marginBottom: 2,
      overflow: 'hidden',
      ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }),
    },
    subNavItemActive: {},
    // Sub accent bar tracks sub-icon size (18) for the same reason.
    subAccentBar: {
      width: 3,
      height: 18,
      borderRadius: 2,
      backgroundColor: 'transparent',
      marginLeft: 2,
    },
    subNavItemLabel: {
      fontSize: 14,
      fontWeight: '400',
      flex: 1,
    },

    // ── Bottom pad ────────────────────────────────────────────────────────────
    bottomPad: { height: 16 },
  });
}

export { SIDEBAR_WIDTH };
